import React from "react";
import {
  Box,
  CircularProgress,
  createStyles,
  Link,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/styles";

import CardCommon from "../../../../components/card/CardCommon";
import { CustomTheme } from "../../../../types/customTheme";
import { formatDuration } from "./historicalUtils";

/**
 * INSIGHT-HIST-13 — station performance side by side.
 *
 * Slowest station first, because the point of this screen is to find the
 * bottleneck. Every row carries its sample count: an average from two orders
 * and one from two hundred must not read the same.
 *
 * Only completed STATION metrics reach this table; a station that never
 * finished a visit produces no row rather than a zero.
 */

const useStyles = makeStyles((theme: CustomTheme) =>
  createStyles({
    headerCell: {
      backgroundColor: theme.palette.background.table_header_background,
      color: theme.palette.custom.orange.contrastText,
      borderBottom: "none",
    },
    cell: {
      color: theme.palette.custom.orange.contrastText,
      borderBottom: `1px solid ${theme.palette.background.entity_border}`,
    },
    selectedCell: {
      color: theme.palette.custom.orange.contrastText,
      borderBottom: `1px solid ${theme.palette.background.entity_border}`,
      backgroundColor: theme.palette.background.entity_highlight_background,
    },
    title: {
      color: theme.palette.custom.orange.contrastText,
      marginBottom: "12px",
    },
    // A plain MUI Link keeps its own primary colour, which disappears against
    // the dark card. The station id must read as clearly as the numbers.
    link: {
      color: theme.palette.custom.orange.contrastText,
      textDecoration: "underline",
      cursor: "pointer",
      "&:hover": {
        color: theme.palette.custom.orange.contrastText,
      },
    },
  }),
);

export type StationAverage = {
  stationId: string;
  completed: number;
  avgDurationMs: number;
  longestDurationMs: number;
};

export interface StationComparisonTableProps {
  stations: StationAverage[];
  isLoading: boolean;
  /** The station whose delayed orders are being shown, when one is chosen. */
  selectedStationId?: string;
  onSelectStation: (stationId: string) => void;
}

const StationComparisonTable: React.FunctionComponent<StationComparisonTableProps> = ({
  stations,
  isLoading,
  selectedStationId,
  onSelectStation,
}) => {
  const classes = useStyles();

  return (
    <CardCommon backgroundColor="entity_background">
      <Box p="20px">
        <Typography variant="h6" className={classes.title}>
          Station performance
        </Typography>

        {isLoading ? (
          <Box display="flex" justifyContent="center" py={3}>
            <CircularProgress size={24} />
          </Box>
        ) : stations.length === 0 ? (
          <Box py={2}>
            <Typography variant="body2">
              No Data. No completed station metrics for the selected dates and
              filters.
            </Typography>
          </Box>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell className={classes.headerCell}>Station</TableCell>
                <TableCell className={classes.headerCell} align="right">
                  Average
                </TableCell>
                <TableCell className={classes.headerCell} align="right">
                  Longest
                </TableCell>
                <TableCell className={classes.headerCell} align="right">
                  Completed
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {stations.map((station) => {
                const cellClass =
                  station.stationId === selectedStationId
                    ? classes.selectedCell
                    : classes.cell;

                return (
                  <TableRow key={station.stationId}>
                    <TableCell className={cellClass}>
                      <Link
                        component="button"
                        variant="body2"
                        className={classes.link}
                        onClick={() => onSelectStation(station.stationId)}
                      >
                        {station.stationId || "Unknown"}
                      </Link>
                    </TableCell>
                    <TableCell className={cellClass} align="right">
                      {formatDuration(station.avgDurationMs)}
                    </TableCell>
                    <TableCell className={cellClass} align="right">
                      {formatDuration(station.longestDurationMs)}
                    </TableCell>
                    <TableCell className={cellClass} align="right">
                      {station.completed}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </Box>
    </CardCommon>
  );
};

export default StationComparisonTable;
